import React from 'react'

import { bool, string } from 'prop-types'

import { formatLongDate } from 'Utils/FormatDate'
import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import CancelConfirmationModal from './Components/CancelConfirmationModal'
import CancelSubscriptionModal from './Components/CancelSubscriptionModal/CancelSubscriptionModal'
import EmptySubscription from './Components/EmptySubscription'
import NoSubscription from './Components/NoSubscription/NoSubscription'
import PauseConfirmationModal from './Components/PauseConfirmationModal'
import PlanChangeModal from './Components/PlanChangeModal/PlanChangeModal'
import CancelInfo from './Components/Subscription/Components/CancelInfo'
import PaymentHistory from './Components/Subscription/Components/PaymentHistory'
import Subscription from './Components/Subscription/Subscription'
import SubscriptionStatusBanner from './Components/SubscriptionStatusBanner'
import SwitchPlanModal from './Components/SwitchPlanModal/SwitchPlanModal'
import { PLAN_TYPE, TIER, isRestructureLayout } from './constants'
import useSubscriptionManagement from './hooks/useSubscriptionManagement'
import {
  availablePlansShape,
  catalogValuesShape,
  paymentHistoryShape,
  plansShape,
  subscriptionPromoSectionShape,
  subscriptionShape,
} from './propTypes'

const PAYMENT_FORM_PATH = '/profile/subscription/payment-form'
const UPGRADE_PATH = '/profile/subscription/upgrade'

function SubscriptionManagement({
  subscription = null,
  paymentHistory = EMPTY_ARRAY,
  subscriptionPromoSection = null,
  locale = 'en',
  subscriptionV16Enabled = false,
  subscriptionPauseEnabled = false,
  plans = null,
  plansRestructureEnabled = false,
  availablePlans = null,
  paymentFormUrl = PAYMENT_FORM_PATH,
  upgradeUrl = UPGRADE_PATH,
  subscriptionCatalogValues = null,
}) {
  const {
    translate,
    subscriptionData,
    subscriptionForDisplay,
    trackingData,
    isUsageDisabled,
    feeData,
    feeError,
    isFeeLoading,
    isCancelModalOpen,
    isRestoreLoading,
    retryState,
    handleCancelClick,
    handleCloseCancel,
    handleCancelSubscription,
    handleCaptureCancellationReason,
    handleRenewClick,
    handleReactivateClick,
    handleRetryClick,
    handleCancelPlanSwitch,
    isCancelPlanSwitchLoading,
    isCancelFlowV16Enabled,
    isFeeFreeFlow,
    cancelEndsNow,
    accessUntilDate,
    showPauseOffer,
    handlePauseRequest,
    isPauseLoading,
    isPauseConfirmationOpen,
    handlePauseConfirmationDone,
    cancelConfirmation,
    handleCancelConfirmationDone,
    handleResumePauseClick,
    isResumeLoading,
    resumeRequested,
    resumeInProgress,
    isSwitchPlanModalOpen,
    previewData,
    handleSwitchPlanClick,
    handleCloseSwitchPlanModal,
    handleSwitchSuccess,
    isPlanChangeModalOpen,
    handleOpenPlanChange,
    handleClosePlanChange,
    scheduleChange,
    isScheduling,
    keepPlan,
    isKeepingPlan,
  } = useSubscriptionManagement({
    subscription,
    locale,
    subscriptionV16Enabled,
    subscriptionPauseEnabled,
    plansRestructureEnabled,
  })

  const isRestructure = isRestructureLayout(subscriptionForDisplay, plansRestructureEnabled)

  const renderContent = () => {
    if (!subscriptionData && plansRestructureEnabled) {
      return (
        <NoSubscription
          availablePlans={availablePlans}
          catalogValues={subscriptionCatalogValues}
          paymentFormUrl={paymentFormUrl}
          showPremiumPlans={!!subscriptionPromoSection?.showPremiumPlans}
          locale={locale}
        />
      )
    }

    if (!subscriptionData) {
      return (
        <EmptySubscription
          locale={locale}
          promoSection={subscriptionPromoSection}
          showPremiumPlans={!!subscriptionPromoSection?.showPremiumPlans}
        />
      )
    }

    return (
      <>
        {!isRestructure && (
          <SubscriptionStatusBanner
            subscription={subscriptionForDisplay}
            locale={locale}
            retryState={retryState}
            resumeInProgress={resumeInProgress}
          />
        )}

        <Subscription
          subscription={subscriptionForDisplay}
          locale={locale}
          isUsageDisabled={isUsageDisabled}
          onCancel={handleCancelClick}
          isCancelLoading={isFeeLoading}
          onReactivate={handleReactivateClick}
          onRenew={handleRenewClick}
          isRestoreLoading={isRestoreLoading}
          updatePaymentUrl={subscriptionData.updatePaymentUrl}
          onRetryPayment={handleRetryClick}
          retryState={retryState}
          onSwitchPlan={isRestructure ? handleOpenPlanChange : handleSwitchPlanClick}
          subscriptionV16Enabled={subscriptionV16Enabled}
          onCancelPlanSwitch={isRestructure ? keepPlan : handleCancelPlanSwitch}
          isCancelPlanSwitchLoading={isRestructure ? isKeepingPlan : isCancelPlanSwitchLoading}
          onResumePause={handleResumePauseClick}
          isResumeLoading={isResumeLoading}
          resumeRequested={resumeRequested}
          resumeInProgress={resumeInProgress}
          plansRestructureEnabled={plansRestructureEnabled}
        />

        {paymentHistory && paymentHistory.length > 0 && (
          <PaymentHistory
            payments={paymentHistory}
            subscriptionId={subscriptionData.id}
            trackingData={trackingData}
            locale={locale}
            plansRestructureEnabled={plansRestructureEnabled}
          />
        )}

        {/* The v1.6 cancel flow restates the consequences inside the modal at the moment of
            intent, so the static settings block would duplicate it. Keep it for flag-off
            cohorts, which never see the consequences step. CGTrader AI keeps it per design. */}
        {!isRestructure && (!isCancelFlowV16Enabled || subscriptionData.tier === TIER.AI_STUDIO) && (
          <CancelInfo
            locale={locale}
            subscriptionV16Enabled={subscriptionV16Enabled}
            planType={subscriptionData.planType}
            gracePeriodEligible={subscriptionData.gracePeriodEligible}
            commitmentReleased={subscriptionData.commitmentReleased}
            tier={subscriptionData.tier}
          />
        )}
      </>
    )
  }

  return (
    <div className="subscription-management">
      <div className="subscription-management__header">
        <h2
          className={plansRestructureEnabled
            ? 'subscription-management__heading cgt-heading-400'
            : 'subscription-management__title'}
        >
          {translate('subscription_management.title')}
        </h2>
        <p className="cgt-legacy-text-default">
          {translate('subscription_management.subtitle')}
        </p>
      </div>
      <div className="subscription-management__content">{renderContent()}</div>
      {subscriptionData && (
        <CancelSubscriptionModal
          isOpen={isCancelModalOpen}
          onClose={handleCloseCancel}
          onSubmit={handleCancelSubscription}
          onReasonCapture={handleCaptureCancellationReason}
          subscriptionId={subscriptionData.id}
          trackingData={trackingData}
          feeData={feeData}
          feeError={feeError}
          isFeeLoading={isFeeLoading}
          locale={locale}
          subscriptionV16Enabled={isCancelFlowV16Enabled}
          planType={subscriptionData.planType}
          gracePeriodEligible={subscriptionData.gracePeriodEligible}
          commitmentReleased={subscriptionData.commitmentReleased}
          showPauseOffer={showPauseOffer}
          oneMonthPause={isRestructure}
          onPause={handlePauseRequest}
          isPauseLoading={isPauseLoading}
          tier={subscriptionData.tier}
          periodEndDate={subscriptionData.periodEndDate}
          isFeeFreeFlow={isFeeFreeFlow}
          plansRestructureEnabled={plansRestructureEnabled}
          cancelEndsNow={cancelEndsNow}
          accessUntilDate={accessUntilDate}
          billingTerm={subscriptionData.billingTerm}
          planChanges={subscriptionData.planChanges}
          essentialsPrice={subscriptionData.billingTerm === PLAN_TYPE.ANNUAL
            ? availablePlans?.essentials?.annualPrice
            : availablePlans?.essentials?.monthlyPrice}
          currentPrice={subscriptionData.termPrice}
          premiumModelCount={subscriptionCatalogValues?.premium}
          premiumGenerations={availablePlans?.premium?.generationsPerMonth}
          downloadedCount={subscriptionData.downloadedCount}
          downloadedValue={subscriptionData.downloadedValue}
          upgradeUrl={upgradeUrl}
          onSchedule={scheduleChange}
          isScheduling={isScheduling}
        />
      )}
      {subscriptionData && (
        <PauseConfirmationModal
          isOpen={isPauseConfirmationOpen}
          onDone={handlePauseConfirmationDone}
          pauseDate={isRestructure
            ? formatLongDate(subscriptionData.pauseOffer?.startsAt, locale, 'UTC')
            : subscriptionData.periodEndDate}
          locale={locale}
        />
      )}
      {cancelConfirmation && (
        <CancelConfirmationModal
          isOpen
          onDone={handleCancelConfirmationDone}
          accessUntil={cancelConfirmation.accessUntil}
          accessUntilLabel={cancelConfirmation.accessUntilLabel}
          cancelFee={cancelConfirmation.cancelFee}
          endsNow={cancelConfirmation.endsNow}
          locale={locale}
          tier={subscriptionData?.tier}
        />
      )}
      {isRestructure && isPlanChangeModalOpen && (
        <PlanChangeModal
          subscription={subscriptionForDisplay}
          availablePlans={availablePlans}
          catalogValues={subscriptionCatalogValues}
          upgradeUrl={upgradeUrl}
          onClose={handleClosePlanChange}
          onSchedule={scheduleChange}
          isScheduling={isScheduling}
          locale={locale}
        />
      )}
      {subscriptionData && plans && previewData && (
        <SwitchPlanModal
          isOpen={isSwitchPlanModalOpen}
          onClose={handleCloseSwitchPlanModal}
          subscription={subscriptionData}
          plans={plans}
          previewData={previewData}
          locale={locale}
          onSwitchSuccess={handleSwitchSuccess}
        />
      )}
    </div>
  )
}

SubscriptionManagement.propTypes = {
  subscription: subscriptionShape,
  paymentHistory: paymentHistoryShape,
  subscriptionPromoSection: subscriptionPromoSectionShape,
  locale: string,
  subscriptionV16Enabled: bool,
  subscriptionPauseEnabled: bool,
  plans: plansShape,
  plansRestructureEnabled: bool,
  availablePlans: availablePlansShape,
  paymentFormUrl: string,
  upgradeUrl: string,
  subscriptionCatalogValues: catalogValuesShape,
}

export default SubscriptionManagement
